import { Keypair } from '@solana/web3.js';
import { ethers } from 'ethers';
import { useLocalStorage } from 'usehooks-ts';
import { toast } from 'sonner';
import bs58 from 'bs58';
import crypto from 'crypto';
import { SuiWalletGenerator } from '@shared/services/helpers/sui/wallet-generation.service';
import { fromHEX } from '@mysten/sui.js/utils';
import { Ed25519Keypair } from '@mysten/sui.js/keypairs/ed25519';
import { useTurnKeyAuth } from './turnkey/useTurnKeyAuth';
import { slackLogger } from '@shared/utils/off-chain/slack-logger';
import { useSession } from '@/contexts/LitSessionProvider';
export default function useGetWrappedWallet() {
  const {
    turnKeyState,
    exportWallet
  } = useTurnKeyAuth();
  const [startDecryption] = useLocalStorage('startDecryption', false);
  const {
    PKP
  } = useSession();
  const [litCachedMinSig, setLitCachedMinSig] = useLocalStorage('genius-lit-cached-min-sig', null);
  const [decryptionSignature] = useLocalStorage('genius-decryption-signature', null);
  const [cipher1, setCipher1] = useLocalStorage('cipher1', null);
  const [cipher2, setCipher2] = useLocalStorage('cipher2', null);
  const [cipher3, setCipher3] = useLocalStorage('cipher3', null);
  const getEVMWallet = async () => {
    var _a;
    if (startDecryption && (!litCachedMinSig || !decryptionSignature || !cipher1 || !cipher2) && !(turnKeyState === null || turnKeyState === void 0 ? void 0 : turnKeyState.user)) {
      toast.info(`You're account is still being set up. Please wait a moment.`);
      return null;
    }
    try {
      if (turnKeyState === null || turnKeyState === void 0 ? void 0 : turnKeyState.user) {
        const pvtKey = await exportWallet('ADDRESS_FORMAT_ETHEREUM');
        const wallet = new ethers.Wallet(pvtKey);
        return wallet;
      } else {
        const evmWalletLC = cipher1;
        if (!evmWalletLC) {
          throw new Error('No EVM wallet found');
          return null;
        } else {
          const encryptionSig = (_a = litCachedMinSig || decryptionSignature) === null || _a === void 0 ? void 0 : _a.slice(3, 35);
          const decryptedPrivateKey = decrypt(evmWalletLC, encryptionSig);
          const wallet = new ethers.Wallet(decryptedPrivateKey);
          return wallet;
        }
      }
    } catch (error) {
      console.log('WUJI getEVMWallet error', error);
      slackLogger({
        type: 'APPLICATION_ERROR',
        payload: {
          type: 'EVM_WALLET_RETRIEVAL_ERROR',
          error: error.message,
          stack: error.stack,
          pkp: PKP,
          turnKeyState
        }
      });
      toast.error('There was an error in retrieving your EVM wallet, please log out and try again.');
    }
  };
  const getSolanaWallet = async () => {
    var _a;
    if (startDecryption && (!litCachedMinSig || !decryptionSignature || !cipher1 || !cipher2) && !(turnKeyState === null || turnKeyState === void 0 ? void 0 : turnKeyState.user)) {
      toast.info(`You're account is still being set up. Please wait a moment.`);
      return null;
    }
    try {
      if (turnKeyState === null || turnKeyState === void 0 ? void 0 : turnKeyState.user) {
        const pvtKey = await exportWallet('ADDRESS_FORMAT_SOLANA');
        const wallet = Keypair.fromSecretKey(bs58.decode(pvtKey));
        return wallet;
      } else {
        const solanaWalletLC = cipher2;
        if (!solanaWalletLC) {
          throw new Error('No Solana wallet found');
          return null;
        } else {
          const encryptionSig = (_a = litCachedMinSig || decryptionSignature) === null || _a === void 0 ? void 0 : _a.slice(3, 35);
          const decryptedPrivateKey = decrypt(solanaWalletLC, encryptionSig);
          const keypair = Keypair.fromSecretKey(bs58.decode(decryptedPrivateKey));
          return keypair;
        }
      }
    } catch (error) {
      console.log('WUJIgetSolanaWallet error', error);
      slackLogger({
        type: 'APPLICATION_ERROR',
        payload: {
          type: 'SOLANA_WALLET_RETRIEVAL_ERROR',
          error: error.message,
          stack: error.stack,
          pkp: PKP,
          turnKeyState
        }
      });
      toast.error('There was an error in retrieving your Solana wallet, please log out and try again.');
    }
  };
  const getSuiWallet = async () => {
    var _a;
    if (startDecryption) {
      toast.info(`You're account is still being set up. Please wait a moment.`);
      return null;
    }
    try {
      if (turnKeyState === null || turnKeyState === void 0 ? void 0 : turnKeyState.user) {
        const pvtKey = await exportWallet('ADDRESS_FORMAT_SUI');
        const privateKeyBuffer = fromHEX(pvtKey);
        const keypair = Ed25519Keypair.fromSecretKey(privateKeyBuffer);
        return keypair;
      } else {
        const suiWalletLC = cipher3;
        if (!suiWalletLC) {
          throw new Error('No Sui wallet found');
          return null;
        } else {
          const encryptionSig = (_a = litCachedMinSig || decryptionSignature) === null || _a === void 0 ? void 0 : _a.slice(3, 35);
          const decryptedMnemonic = decrypt(suiWalletLC, encryptionSig);
          const wallet = SuiWalletGenerator.generateWalletFromMnemonic(decryptedMnemonic);
          const keypair = wallet.keypair;
          if (!keypair) {
            throw new Error('Error in retrieving Sui wallet');
          }
          return keypair;
        }
      }
    } catch (error) {
      console.log('WUJIgetSuiWallet error', error);
      slackLogger({
        type: 'APPLICATION_ERROR',
        payload: {
          type: 'SUI_WALLET_RETRIEVAL_ERROR',
          error: error.message,
          stack: error.stack,
          pkp: PKP,
          turnKeyState
        }
      });
      toast.error('There was an error in retrieving your Sui wallet, please log out and try again.');
    }
  };
  return {
    getEVMWallet,
    getSolanaWallet,
    getSuiWallet,
    litCachedMinSig,
    setLitCachedMinSig,
    cipher1,
    setCipher1,
    cipher2,
    setCipher2,
    cipher3,
    setCipher3
  };
}
const algorithm = 'aes-256-ctr';
export const encrypt = (text, secretKey) => {
  const iv = crypto.randomBytes(16);
  const cipher = crypto.createCipheriv(algorithm, secretKey, iv);
  const encrypted = Buffer.concat([cipher.update(text), cipher.final()]);
  return {
    iv: iv === null || iv === void 0 ? void 0 : iv.toString('hex'),
    content: encrypted === null || encrypted === void 0 ? void 0 : encrypted.toString('hex')
  };
};
export const decrypt = (hash, secretKey) => {
  const decipher = crypto.createDecipheriv(algorithm, secretKey, Buffer.from(hash.iv, 'hex'));
  const decrpyted = Buffer.concat([decipher.update(Buffer.from(hash.content, 'hex')), decipher.final()]);
  return decrpyted === null || decrpyted === void 0 ? void 0 : decrpyted.toString();
};